'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect } from 'react';
import { useLabsRuntime } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { labsDraftStorageKey } from '@/app/(platform)/(subscription-gated)/labs/_engine/store';
import { getPreviewVisitorId } from './anonymous-id';
import { PREVIEW_STORE_VARIANT } from './preview-steps';

/**
 * Restores a returning reader's draft. The anonymous cousin of `LabsContainer`, with one deliberate
 * difference that matters more than everything else in this file.
 *
 * 🔴 IT PAINTS IMMEDIATELY. IT DOES NOT GATE RENDER ON HYDRATION.
 *
 * The sprint's container returns `null` until the persisted draft has loaded, which is right there:
 * every user is signed in, most have a draft, and a flash of the wrong step is worse than a beat of
 * nothing. Copying that here was a mistake, and a measurable one. Because the gate sat above the whole
 * tree, the prerendered HTML for this route contained NO LAB AT ALL. 33KB of script tags and not one
 * word of content. A student on a phone on campus wifi, tapping a link from a Google result, got a
 * white screen until the JavaScript downloaded, parsed and hydrated.
 *
 * For this audience that is the worst possible first impression, and it is self-inflicted: the draft
 * is a returning-visitor convenience, and almost nobody here is a returning visitor. So the tree
 * renders straight away with the store's default state, which is `step: 'welcome'` — exactly what a
 * first-time reader should see — and the draft is restored underneath.
 *
 * WHAT THIS COSTS, honestly: a reader who left mid-lab and comes back sees the welcome screen for one
 * frame before jumping to where they were. That is a real regression for a small group, traded for a
 * first paint for everyone. On a public page reached from search, that trade is not close.
 *
 * The visitor id is no longer resolved here either. It is a synchronous localStorage read, so
 * `PreviewApp` takes it in a lazy initializer and every event carries it from the first render. This
 * container owning it was the reason it had to withhold children in the first place.
 */
export function PreviewContainer(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  const {
    pack,
    store
  } = useLabsRuntime();
  let t1;
  let t2;
  if ($[0] !== pack || $[1] !== store) {
    t1 = () => {
      let isMounted = true;
      const visitorId = getPreviewVisitorId();
      if (!visitorId) {
        return;
      }
      const persistApi = store.persist;
      const storageKey = `${labsDraftStorageKey(pack, PREVIEW_STORE_VARIANT)}:${visitorId}`;
      persistApi.setOptions({
        name: storageKey
      });
      const {
        storage
      } = persistApi.getOptions();
      Promise.resolve(storage?.getItem(storageKey)).then(storedValue => {
        if (!isMounted || storedValue == null) {
          return;
        }
        return Promise.resolve(persistApi.rehydrate());
      }).catch(_temp);
      return () => {
        isMounted = false;
      };
    };
    t2 = [pack, store];
    $[0] = pack;
    $[1] = store;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return children;
}
function _temp() {}